/**
 * Flow manifest schema and validation.
 *
 * Moved from @skaile/workspaces/types/manifests/flow.ts in Phase 1.7.3.
 * This is now the canonical location for flow manifest validation.
 */

import * as z from "zod";

// ---------------------------------------------------------------------------
// Validation result (inline — avoids importing from types/manifests/_shared)
// ---------------------------------------------------------------------------

export interface ManifestValidationSuccess<T> {
  ok: true;
  data: T;
}

export interface ManifestValidationFailure {
  ok: false;
  errors: Array<{ path: string; message: string }>;
}

export type ManifestValidationResult<T> = ManifestValidationSuccess<T> | ManifestValidationFailure;

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const FlowNodeSchema = z.looseObject({
  id: z.string().min(1),
  skill: z.string().optional(),
  type: z.string().optional(),
  label: z.string().optional(),
  description: z.string().optional(),
  data: z
    .looseObject({
      optional: z.boolean().optional(),
      // Mandatory approval gate — autonomous mode cannot skip it.
      approval: z.looseObject({ mandatory: z.boolean().optional() }).optional(),
    })
    .refine((d) => !(d.optional === true && d.approval?.mandatory === true), {
      // A mandatory gate can never be skipped, so optional+mandatory is a
      // contradiction — reject it rather than silently letting mandatory win.
      message: "a node cannot be both optional and a mandatory approval gate",
    })
    .optional(),
});

const FlowEdgeSchema = z.looseObject({
  id: z.string().optional(),
  source: z.string().min(1),
  target: z.string().min(1),
  condition: z.string().optional(),
});

export const FlowManifestSchema = z.looseObject({
  id: z.string().min(1),
  version: z.string().optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  metadata: z
    .looseObject({
      tags: z.array(z.string()).optional(),
      stage: z.string().optional(),
      icon: z.string().optional(),
      category: z.string().optional(),
      onboarding: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
  globals: z.record(z.string(), z.unknown()).optional(),
  modes: z.record(z.string(), z.unknown()).optional(),
  tier_presets: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
  artifact_handoff: z.unknown().optional(),
  entry: z.string().optional(),
  nodes: z.array(FlowNodeSchema).optional(),
  edges: z.array(FlowEdgeSchema).optional(),
});

export type FlowManifest = z.infer<typeof FlowManifestSchema>;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function validateFlow(data: unknown): ManifestValidationResult<FlowManifest> {
  const result = FlowManifestSchema.safeParse(data);
  if (result.success) {
    return { ok: true, data: result.data };
  }
  return {
    ok: false,
    errors: result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    })),
  };
}
